import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Dropdown,
  Form,
  InputGroup,
  Row,
} from "react-bootstrap";
import { CheckLg } from "react-bootstrap-icons";
import { toAbsoluteUrl } from "../../helpers";
import { formatCurrency } from "../../utilities";
import { BiMinus, BiPlus } from "react-icons/bi";
import { StoreProductModel, StoreModel } from "../../models";

const MinQuantity = 1;
const MaxQuantity = 100;

type Props = {
  product: StoreProductModel;
  store: StoreModel | undefined;
  amount: number;
  quantity: number;
  selected: string | undefined;
  onQuantityChange: (quantity: number) => void;
  onModifierChange: (value: string) => void;
  onSubmit: (product: StoreProductModel) => void;
};

export const GiftcardForm = ({
  product,
  store,
  amount: giftCardAmount,
  selected: modifierId,
  quantity,
  onQuantityChange,
  onModifierChange,
  onSubmit,
}: Props) => {
  const handleQuantityIncrease = () => {
    onQuantityChange(quantity < MaxQuantity ? quantity + 1 : quantity);
  };

  const handleQuantityDecrease = () => {
    onQuantityChange(quantity > MinQuantity ? quantity - 1 : quantity);
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = Number(e.target.value);
    if (value < MinQuantity) value = MinQuantity;
    if (value > MaxQuantity) value = MaxQuantity;
    onQuantityChange(value);
  };

  const allModifiers = product.modifier_group.flatMap((group) =>
    group.modifier.map((modifier) => ({
      id: modifier.m_id,
      name: modifier.m_name,
    })),
  );

  const selectedModifier = allModifiers.find((m) => m.id === modifierId);

  return (
    <Card className="transparent-bg shadow-none border-none">
      <CardBody className="p-4 giftcard-wrapper">
        <Row className="align-items-center gx-3 gx-md-4 gx-lg-5 gy-4">
          <Col xs={12} md={12} lg={6} className="giftcard-left">
            <div className="restaurant-gift-card">
              <div className="giftcard-badge">
                <img
                  src={toAbsoluteUrl("/images/gift_icon.svg")}
                  alt=""
                  className="giftcard-badge-icon"
                />
                <span>Gift Card</span>
              </div>

              <div className="giftcard-restaurant-name">
                <strong>{store?.store_name}</strong>
              </div>
              <div className="giftcard-center-content">
                <img
                  src={toAbsoluteUrl("/images/gift_icon.svg")}
                  alt=""
                  className="giftcard-main-icon"
                />
                <h3>GIFT CARD</h3>
                <p>A perfect treat for any occasion</p>
              </div>
              <div className="giftcard-bottom-strip">
                <div>
                  <small>Card Value</small>
                  <h4>{formatCurrency(giftCardAmount)}</h4>
                </div>

                <div className="giftcard-divider"></div>

                <div className="giftcard-validity">Valid Forever</div>
              </div>
            </div>
          </Col>
          <Col
            xs={12}
            md={12}
            lg={6}
            className="d-flex flex-column giftcard-right"
          >
            <CardTitle className="text-uppercase">
              {product.product_name}
            </CardTitle>
            <div className="mb-2">
              <Form.Label>Amount:</Form.Label>
              <Dropdown
                className="border-0"
                onSelect={(value) => value && onModifierChange(value)}
              >
                <Dropdown.Toggle
                  as="div"
                  role="button"
                  className="d-flex align-items-center justify-content-between gap-2"
                  style={{
                    border: "1px solid var(--border-soft)",
                    borderRadius: 5,
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: "var(--fsize-1)",
                    color: "var(--text-primary)",
                    background: "var(--bg-surface, #fff)",
                    userSelect: "none",
                  }}
                >
                  {selectedModifier ? (
                    <span style={{ fontWeight: 600 }}>
                      {selectedModifier.name}
                    </span>
                  ) : (
                    <span style={{ color: "var(--text-muted, #888)" }}>
                      Select Amount
                    </span>
                  )}
                </Dropdown.Toggle>

                <Dropdown.Menu
                  className="w-100 p-1 loyalty-dropdown-menu"
                  style={{
                    boxShadow:
                      "0 4px 16px rgba(var(--brand-primary-soft-rgb), 0.18)",
                    border: "1px solid var(--border-light)",
                    borderRadius: 8,
                    maxHeight: 220,
                    overflowY: "auto",
                  }}
                >
                  {allModifiers.map((modifier) => {
                    const isActive = modifier.id === modifierId;
                    return (
                      <Dropdown.Item
                        key={modifier.id}
                        eventKey={modifier.id}
                        className="rounded"
                        style={{
                          padding: "8px 12px",
                          background: isActive
                            ? "rgba(var(--brand-primary-soft-rgb), 0.10)"
                            : undefined,
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between gap-2">
                          <span style={{ fontWeight: isActive ? 600 : 400 }}>
                            {modifier.name}
                          </span>
                          {isActive && (
                            <CheckLg size={13} color="var(--brand-primary)" />
                          )}
                        </div>
                      </Dropdown.Item>
                    );
                  })}
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <div className="mb-4">
              <Form.Label>Quantity:</Form.Label>
              <InputGroup className="giftcard-quantity">
                <Button onClick={handleQuantityDecrease}>
                  <BiMinus size={20} />
                </Button>
                <Form.Control
                  className="text-center"
                  type="number"
                  step={1}
                  min={MinQuantity}
                  max={MaxQuantity}
                  value={quantity}
                  onChange={handleQuantityChange}
                  required
                />
                <Button onClick={handleQuantityIncrease}>
                  <BiPlus size={20} />
                </Button>
              </InputGroup>
            </div>
            <div>
              <Button
                className="text-uppercase w-100 btn-lg giftcard-purchase-btn"
                onClick={() => onSubmit(product)}
              >
                Purchase
              </Button>
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};
